import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { Box, Step, StepLabel, Stepper } from "@mui/material";
import { PartCreationWizardState, PartCreationWizardValidationState, WizardStep } from "./types.ts";
import { CatalogPartFormStep } from "./steps/CatalogPartFormStep.tsx";
import { CatalogPartBomStep } from "./steps/CatalogPartBomStep.tsx";
import { SitePartFormStep } from "./steps/SitePartFormStep.tsx";
import { SitePartOperationsStep } from "./steps/SitePartOperationsStep.tsx";
import { SalesPartFormStep } from "./steps/SalesPartFormStep.tsx";
import { SupplierPartFormStep } from "./steps/SupplierPartFormStep.tsx";
import { PartWarehouseLocationStep } from "./steps/PartWarehouseLocationStep.tsx";
import i18n from "i18next";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import {
	PartCreationWizardApi,
	PartCreationWizardApi_FormInitData,
} from "src/api/generated/erp/parts/catalogPart/api/partCreationWizardApi.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { faAngleLeft, faAngleRight, faSave, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { useStoredState } from "src/utils/useStoredState.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { ConfigurationDefaultPricingMethod } from "src/api/generated/erp/db/types/enums/configurationDefaultPricingMethod.ts";
import { convertWizardStateToSubmitData } from "./validation.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { confirmConfiguratorAppVersionWarningForNewCatalogPart } from "src/components/views/erp/configurator/managing/configuratorAppVersionWarning.ts";

interface PartCreationWizardProps {
	onClose: () => void;
	onComplete: (result: { catalogPartId: number }) => void;
	templateCatalogPartId?: number;
	defaultPartCategoryId?: number;
	onContentEdited?: () => void;
}

export const PartCreationWizard = (props: PartCreationWizardProps) => {
	const { templateCatalogPartId } = props;
	const { defaultSiteId } = useGlobalInitData();
	return (
		<AsyncFetchRender
			fetch={async () =>
				await PartCreationWizardApi.getInitData({
					siteId: defaultSiteId,
					templateCatalogPartId: templateCatalogPartId,
				})
			}
			content={(initData) => <PartCreationWizardContent {...props} initData={initData} />}
		/>
	);
};

interface PartCreationWizardContentProps extends PartCreationWizardProps {
	initData: PartCreationWizardApi_FormInitData;
}

const PartCreationWizardContent = ({
	onClose,
	onComplete,
	initData,
	onContentEdited,
	defaultPartCategoryId,
}: PartCreationWizardContentProps) => {
	const { appUserId } = useGlobalInitData();

	const [storedValues, setStoredValues] = useStoredState<StoredValues>({
		defaultValue: {
			partNumberInsertType: "numberSequenceId",
			publishCatalogPartRevision: false,
		},
		key: "A138A7888AD9E9C5",
		type: "object",
	});
	const [wizardState, setWizardState] = useState<PartCreationWizardState>(() =>
		getDefaultWizardState({
			appUserId,
			defaultPartCategoryId,
			storedValues,
			templateCatalogPart: initData.templateCatalogPart,
		}),
	);
	const [validationState, setValidationState] = useState<PartCreationWizardValidationState>({
		"catalog-part-form": { isValid: false },
		"catalog-part-bom": { isValid: true },
		"site-part-form": { isValid: true },
		"site-part-operations": { isValid: true },
		"part-warehouse-location": { isValid: true },
		"sales-part-form": { isValid: true },
		"supplier-part-form": { isValid: true },
	});
	const sitePartStepVisitedRef = useRef(false);
	const partWarehouseLocationStepVisitedRef = useRef(false);
	const salesPartStepVisitedRef = useRef(false);
	const supplierPartStepVisitedRef = useRef(false);
	const showMessageDialog = useMessageDialog();
	const showConfirmDialog = useConfirmDialog();

	const enabledSteps = steps.filter((step) => {
		if (step.id == "sales-part-form") {
			return wizardState.createSitePart;
		}
		if (step.id == "site-part-operations") {
			return wizardState.createSitePart && wizardState.sitePart.partType === "MANUFACTURE";
		}
		if (step.id == "supplier-part-form") {
			return wizardState.createSitePart && wizardState.sitePart.partType === "PURCHASE";
		}
		if (step.id == "part-warehouse-location") {
			return wizardState.createSitePart;
		}
		return true;
	});

	const currentStepIndex = Math.max(0, enabledSteps.findIndex((step) => step.id === wizardState.currentStepId));
	const currentStep = enabledSteps[currentStepIndex] ?? steps[0]!;
	const isFirstStep = currentStepIndex === 0;
	const isLastStep = currentStepIndex === enabledSteps.length - 1;
	const currentStepHasErrors = !(validationState[currentStep.id]?.isValid ?? true);
	const anyStepHasErrors = enabledSteps.some((step) => !(validationState[step.id]?.isValid ?? true));

	const handleValidationChange = useCallback(
		(isValid: boolean) => {
			setValidationState((prev) => ({
				...prev,
				[currentStep.id]: {
					isValid,
				},
			}));
		},
		[setValidationState, currentStep.id],
	);

	return (
		<VerticalBox sx={{ flex: 1, padding: 1, gap: 1 }}>
			<VerticalBox sx={{ flex: 1, gap: 2 }}>
				<Stepper activeStep={currentStepIndex}>
					{enabledSteps.map((step) => {
						return (
							<Step key={step.id} completed={false}>
								<StepLabel>{step.label}</StepLabel>
							</Step>
						);
					})}
				</Stepper>
				<StepContent
					currentStep={currentStep}
					wizardState={wizardState}
					initData={initData}
					onValidationChange={handleValidationChange}
					setWizardState={setWizardState}
					onContentEdited={onContentEdited}
					setStoredValues={setStoredValues}
				/>
			</VerticalBox>
			<HorizontalBox sx={{ gap: 2, justifyContent: "flex-end", paddingRight: 1 }}>
				<Box sx={{ flex: 1 }} />
				<AsyncButton onClick={() => onClose()} label={i18n.t("cancel")} icon={faTimes} />
				{!isFirstStep && <AsyncButton onClick={() => handleBack()} label={i18n.t("back")} icon={faAngleLeft} />}
				<AsyncButton
					onClick={() => handleComplete()}
					label={i18n.t("save")}
					icon={faSave}
					disabled={anyStepHasErrors}
					tooltip={getSaveButtonTooltip(wizardState, anyStepHasErrors)}
				/>
				{!isLastStep && (
					<AsyncButton
						variant="contained"
						onClick={() => handleNext()}
						label={i18n.t("next")}
						icon={faAngleRight}
						disabled={
							isLastStep ? true
							: currentStepHasErrors ?
								i18n.t("please_fix_validation_errors_before_saving")
							:	false
						}
					/>
				)}
			</HorizontalBox>
		</VerticalBox>
	);

	function handleNext() {
		const nextStepIndex = currentStepIndex + 1;
		const nextStepId = enabledSteps[nextStepIndex]?.id;
		if (nextStepId == null) return;
		setWizardState((prev) => ({
			...prev,
			currentStepId: nextStepId,
		}));
		// On first visit on site part step, mark createSitePart as true
		// Same for sales and supplier part steps
		if (nextStepId == "site-part-form") {
			if (!sitePartStepVisitedRef.current) {
				setWizardState((prev) => ({
					...prev,
					createSitePart: true,
				}));
				sitePartStepVisitedRef.current = true;
			}
		}
		if (nextStepId == "part-warehouse-location") {
			if (!partWarehouseLocationStepVisitedRef.current) {
				setWizardState((prev) => ({
					...prev,
					createPartWarehouseLocation: true,
				}));
				partWarehouseLocationStepVisitedRef.current = true;
			}
		}
		if (nextStepId == "sales-part-form") {
			if (!salesPartStepVisitedRef.current) {
				setWizardState((prev) => ({
					...prev,
					createSalesPart: true,
				}));
				salesPartStepVisitedRef.current = true;
			}
		}
		if (nextStepId == "supplier-part-form") {
			if (!supplierPartStepVisitedRef.current) {
				setWizardState((prev) => ({
					...prev,
					createSupplierPart: true,
				}));
				supplierPartStepVisitedRef.current = true;
			}
		}
	}

	function handleBack() {
		const prevStepIndex = currentStepIndex - 1;
		const prevStepId = enabledSteps[prevStepIndex]?.id;
		if (prevStepId == null) return;
		setWizardState((prev) => ({
			...prev,
			currentStepId: prevStepId,
		}));
	}

	async function handleComplete() {
		// Check if any step has validation errors
		if (anyStepHasErrors) {
			showMessageDialog({
				title: i18n.t("error"),
				content: i18n.t("please_fix_validation_errors_before_saving"),
			});
			return;
		}

		const submitData = convertWizardStateToSubmitData(wizardState);
		const { isConfigurable, configurationProductFamilyId } = submitData.catalogPart;
		if (
			wizardState.publishCatalogPartRevision &&
			isConfigurable &&
			configurationProductFamilyId != null &&
			!(await confirmConfiguratorAppVersionWarningForNewCatalogPart(showConfirmDialog, configurationProductFamilyId))
		)
			return;

		const result = await PartCreationWizardApi.submit({
			catalogPart: submitData.catalogPart,
			catalogPartBom: submitData.catalogPartBom,
			sitePart: submitData.sitePart,
			sitePartOperations: submitData.sitePartOperations,
			salesPart: submitData.salesPart,
			supplierPart: submitData.supplierPart,
			partWarehouseLocation: submitData.partWarehouseLocation,
			templateCatalogPartId: wizardState.templateCatalogPartId,
			templateCatalogPartRevisionId: wizardState.templateCatalogPartRevisionId,
			publishCatalogPartRevision: wizardState.publishCatalogPartRevision,
		});

		onComplete({ catalogPartId: result.catalogPartId });
	}
};

const getSaveButtonTooltip = (wizardState: PartCreationWizardState, anyStepHasErrors: boolean): string => {
	if (anyStepHasErrors) return i18n.t("please_fix_validation_errors_before_saving");

	const parts: string[] = [i18n.t("catalog_part").toLowerCase()];
	if (wizardState.catalogPartBom.length > 0) {
		parts.push(i18n.t("bom").toLowerCase());
	}
	if (wizardState.createSitePart) {
		parts.push(i18n.t("site_part").toLowerCase());
	}
	if (wizardState.sitePartOperations.length > 0) {
		parts.push(i18n.t("operations").toLowerCase());
	}
	if (wizardState.createSalesPart) {
		parts.push(i18n.t("sales_part").toLowerCase());
	}
	if (wizardState.createSupplierPart) {
		parts.push(i18n.t("supplier_part").toLowerCase());
	}
	if (wizardState.createPartWarehouseLocation) {
		parts.push(i18n.t("warehouse_location").toLowerCase());
	}

	return i18n.t("save") + " " + parts.join(", ");
};

interface StepContentProps {
	currentStep: WizardStep;
	wizardState: PartCreationWizardState;
	initData: PartCreationWizardApi_FormInitData;
	onValidationChange: (isValid: boolean) => void;
	setWizardState: Dispatch<SetStateAction<PartCreationWizardState>>;
	onContentEdited?: () => void;
	setStoredValues: Dispatch<SetStateAction<StoredValues>>;
}

const StepContent = ({
	currentStep,
	wizardState,
	initData,
	onValidationChange,
	setWizardState,
	onContentEdited,
	setStoredValues,
}: StepContentProps) => {
	const updateCatalogPart = useCallback(
		(updates: Partial<PartCreationWizardState["catalogPart"]>) => {
			setWizardState((prev) => ({
				...prev,
				catalogPart: { ...prev.catalogPart, ...updates },
			}));
			onContentEdited?.();
			setStoredValues((prev) => ({
				...prev,
				partNumberInsertType: updates.partNumberInsertType,
				numberSequenceId: updates.numberSequenceId,
			}));
		},
		[setWizardState, onContentEdited, setStoredValues],
	);

	const updatePublishCatalogPartRevision = useCallback(
		(publishCatalogPartRevision: boolean) => {
			setWizardState((prev) => ({
				...prev,
				publishCatalogPartRevision: publishCatalogPartRevision,
			}));
			onContentEdited?.();
			setStoredValues((prev) => ({
				...prev,
				publishCatalogPartRevision: publishCatalogPartRevision,
			}));
		},
		[setWizardState, onContentEdited, setStoredValues],
	);

	const updateCatalogPartBom = useCallback(
		(bom: PartCreationWizardState["catalogPartBom"]) => {
			setWizardState((prev) => ({
				...prev,
				catalogPartBom: bom,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateCreateSitePart = useCallback(
		(createSitePart: boolean) => {
			setWizardState((prev) => ({
				...prev,
				createSitePart,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateSitePart = useCallback(
		(updates: Partial<PartCreationWizardState["sitePart"]>) => {
			setWizardState((prev) => ({
				...prev,
				sitePart: { ...prev.sitePart, ...updates },
				...(updates.partType !== undefined && updates.partType !== "PURCHASE" ?
					{
						createSupplierPart: false,
						supplierPart: {},
					}
				:	{}),
				...(updates.partType !== undefined && updates.partType !== "MANUFACTURE" ?
					{
						sitePartOperations: [],
					}
				:	{}),
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateSitePartOperations = useCallback(
		(operations: PartCreationWizardState["sitePartOperations"]) => {
			setWizardState((prev) => ({
				...prev,
				sitePartOperations: operations,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateCreateSalesPart = useCallback(
		(createSalesPart: boolean) => {
			setWizardState((prev) => ({
				...prev,
				createSalesPart,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateSalesPart = useCallback(
		(updates: Partial<PartCreationWizardState["salesPart"]>) => {
			setWizardState((prev) => ({
				...prev,
				salesPart: { ...prev.salesPart, ...updates },
			}));
			onContentEdited?.();
			if (updates.configurationDefaultPricingMethod != null) {
				setStoredValues((prev) => ({
					...prev,
					configurationDefaultPricingMethod: updates.configurationDefaultPricingMethod,
				}));
			}
		},
		[setWizardState, onContentEdited, setStoredValues],
	);

	const updateCreateSupplierPart = useCallback(
		(createSupplierPart: boolean) => {
			setWizardState((prev) => ({
				...prev,
				createSupplierPart,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateSupplierPart = useCallback(
		(updates: Partial<PartCreationWizardState["supplierPart"]>) => {
			setWizardState((prev) => ({
				...prev,
				supplierPart: { ...prev.supplierPart, ...updates },
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updateCreatePartWarehouseLocation = useCallback(
		(createPartWarehouseLocation: boolean) => {
			setWizardState((prev) => ({
				...prev,
				createPartWarehouseLocation,
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const updatePartWarehouseLocation = useCallback(
		(updates: Partial<PartCreationWizardState["partWarehouseLocation"]>) => {
			setWizardState((prev) => ({
				...prev,
				partWarehouseLocation: { ...prev.partWarehouseLocation, ...updates },
			}));
			onContentEdited?.();
		},
		[setWizardState, onContentEdited],
	);

	const onSelectCatalogPartTemplate = useCallback(
		(templateCatalogPartId: number) => {
			setWizardState((prev) => ({
				...prev,
				templateCatalogPartId: templateCatalogPartId,
				templateCatalogPartRevisionId: undefined,
				templateSitePartId: undefined,
			}));
		},
		[setWizardState],
	);

	const onSelectCatalogPartRevisionTemplate = useCallback(
		(templateCatalogPartRevisionId: number) => {
			setWizardState((prev) => ({
				...prev,
				templateCatalogPartRevisionId,
			}));
		},
		[setWizardState],
	);

	const onSelectSitePartTemplate = useCallback(
		(templateSitePartId: number) => {
			setWizardState((prev) => ({
				...prev,
				templateSitePartId: templateSitePartId,
				templateSitePartRevisionId: undefined,
			}));
		},
		[setWizardState],
	);

	const onSelectSitePartRevisionTemplate = useCallback(
		(templateSitePartRevisionId: number) => {
			setWizardState((prev) => ({
				...prev,
				templateSitePartRevisionId,
			}));
		},
		[setWizardState],
	);

	switch (currentStep.id) {
		case "catalog-part-form":
			return (
				<CatalogPartFormStep
					initData={initData}
					catalogPart={wizardState.catalogPart}
					updateCatalogPart={updateCatalogPart}
					onSelectTemplate={onSelectCatalogPartTemplate}
					onValidationChange={onValidationChange}
					publishCatalogPartRevision={wizardState.publishCatalogPartRevision}
					onUpdatePublishCatalogPartRevision={updatePublishCatalogPartRevision}
				/>
			);
		case "catalog-part-bom":
			return (
				<CatalogPartBomStep
					bomLines={wizardState.catalogPartBom}
					onUpdateBom={updateCatalogPartBom}
					templateCatalogPartId={wizardState.templateCatalogPartId}
					templateCatalogPartRevisionId={wizardState.templateCatalogPartRevisionId}
					onValidationChange={onValidationChange}
					onSelectTemplateRevision={onSelectCatalogPartRevisionTemplate}
					partIsConfigurable={wizardState.catalogPart.isConfigurable ?? false}
				/>
			);
		case "site-part-form":
			return (
				<SitePartFormStep
					catalogPart={wizardState.catalogPart}
					createSitePart={wizardState.createSitePart}
					onUpdateCreateSitePart={updateCreateSitePart}
					sitePart={wizardState.sitePart}
					onUpdateSitePart={updateSitePart}
					templateSitePartId={wizardState.templateSitePartId}
					templateCatalogPartId={wizardState.templateCatalogPartId}
					onSelectTemplate={onSelectSitePartTemplate}
					erpUnitOptions={initData.erpUnitOptions}
					productionLineOptions={initData.productionLineOptions}
					onValidationChange={onValidationChange}
				/>
			);
		case "site-part-operations":
			return (
				<SitePartOperationsStep
					operationOptions={initData.operationOptions}
					sitePartOperations={wizardState.sitePartOperations}
					onUpdateSitePartOperations={updateSitePartOperations}
					partIsConfigurable={wizardState.catalogPart.isConfigurable ?? false}
					templateSitePartId={wizardState.templateSitePartId}
					templateCatalogPartRevisionId={wizardState.templateCatalogPartRevisionId}
					templateSitePartRevisionId={wizardState.templateSitePartRevisionId}
					onSelectTemplateRevision={onSelectSitePartRevisionTemplate}
					onValidationChange={onValidationChange}
				/>
			);
		case "sales-part-form":
			return (
				<SalesPartFormStep
					catalogPart={wizardState.catalogPart}
					sitePart={wizardState.sitePart}
					createSalesPart={wizardState.createSalesPart}
					onUpdateCreateSalesPart={updateCreateSalesPart}
					salesPart={wizardState.salesPart}
					onUpdateSalesPart={updateSalesPart}
					initData={initData}
					onValidationChange={onValidationChange}
				/>
			);
		case "supplier-part-form":
			return (
				<SupplierPartFormStep
					catalogPart={wizardState.catalogPart}
					createSupplierPart={wizardState.createSupplierPart}
					onUpdateCreateSupplierPart={updateCreateSupplierPart}
					supplierPart={wizardState.supplierPart}
					onUpdateSupplierPart={updateSupplierPart}
					initData={initData}
					onValidationChange={onValidationChange}
				/>
			);
		case "part-warehouse-location":
			return (
				<PartWarehouseLocationStep
					createPartWarehouseLocation={wizardState.createPartWarehouseLocation}
					onUpdateCreatePartWarehouseLocation={updateCreatePartWarehouseLocation}
					partWarehouseLocation={wizardState.partWarehouseLocation}
					onUpdatePartWarehouseLocation={updatePartWarehouseLocation}
					initData={initData}
					onValidationChange={onValidationChange}
				/>
			);
		default:
			return null;
	}
};

const steps: WizardStep[] = [
	{ id: "catalog-part-form", label: i18n.t("part_creation_wizard_step_catalog_part_form") },
	{ id: "catalog-part-bom", label: i18n.t("part_creation_wizard_step_catalog_part_bom") },
	{ id: "site-part-form", label: i18n.t("part_creation_wizard_step_site_part_form") },
	{ id: "site-part-operations", label: i18n.t("part_creation_wizard_step_site_part_operations") },
	{ id: "part-warehouse-location", label: i18n.t("warehouse_location") },
	{ id: "supplier-part-form", label: i18n.t("supplier") },
	{ id: "sales-part-form", label: i18n.t("sales_part") },
];

const getDefaultWizardState = ({
	appUserId,
	defaultPartCategoryId,
	storedValues,
	templateCatalogPart,
}: {
	appUserId: number;
	defaultPartCategoryId: number | undefined;
	storedValues: StoredValues;
	templateCatalogPart: CatalogPart | undefined | null;
}): PartCreationWizardState => ({
	currentStepId: "catalog-part-form",
	catalogPart: {
		partNumberInsertType: storedValues.partNumberInsertType ?? "numberSequenceId",
		numberSequenceId: storedValues.numberSequenceId ?? null,
		manuallyInsertedPartNumber: "",
		...(templateCatalogPart != null ? templateCatalogPart : (
			{
				description_1: "",
				description_2: "",
				manufacturerPartNumber: "",
				additionalNumber: "",
				designUnitFactor: 1,
				weight: 0,
				isConfigurable: false,
				responsiblePersonId: appUserId,
				categoryId: defaultPartCategoryId,
			}
		)),
	},
	catalogPartBom: [],
	publishCatalogPartRevision: storedValues.publishCatalogPartRevision ?? false,
	createSitePart: false,
	sitePart: {},
	sitePartOperations: [],
	createSalesPart: false,
	salesPart: {
		configurationDefaultPricingMethod: storedValues.configurationDefaultPricingMethod ?? "CONSTANT_PRICE",
		salesPrice: 0,
	},
	createSupplierPart: false,
	supplierPart: {},
	createPartWarehouseLocation: false,
	partWarehouseLocation: {},
	templateCatalogPartId: templateCatalogPart?.catalogPartId,
	templateCatalogPartRevisionId: undefined,
	templateSitePartId: undefined,
	templateSitePartRevisionId: undefined,
});

interface StoredValues extends Partial<
	Pick<PartCreationWizardState["catalogPart"], "partNumberInsertType" | "numberSequenceId">
> {
	publishCatalogPartRevision?: boolean;
	configurationDefaultPricingMethod?: ConfigurationDefaultPricingMethod;
}
